import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { getCountryData } from "src/services/countryService";
import { getBilling, updateBilling } from "src/ui/containers/account/api";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { ProfileBillingAddress } from "@dashboard/devices/types";
import { Box } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { SectionFormStack, SectionInputSlot } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { validationSchema } from "./BillingEditForm.validation";

const useStyles = makeStyles()(() => ({
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 420,
    },
}));

type FormValues = ProfileBillingAddress;

type BillingEditFormProps = {
    billingAddress: API.BillingAddress | undefined;
    onSuccess: () => void;
    onCancel: () => void;
};

export const BillingEditForm = ({ billingAddress, onSuccess, onCancel }: BillingEditFormProps) => {
    const [profile, setProfile] = useState<ProfileBillingAddress | undefined>(billingAddress);
    const [isLoading, setLoading] = useState(false);
    const [countryList, setCountryList] = useState<SelectOption[]>([]);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const validate = useValidationSchema(validationSchema);

    const onSubmit = async (values: FormValues) => {
        try {
            const { message } = await updateBilling(values);
            showSuccessToast(message);
            onSuccess();
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    };

    useEffect(() => {
        const fetchBillingAndCountries = async () => {
            setLoading(true);

            const countryListData = await getCountryData();
            setCountryList(countryListData.countryList);

            try {
                const data = await getBilling();
                setProfile(data);
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message);
            } finally {
                setLoading(false);
            }
        };

        void fetchBillingAndCountries();
    }, []);

    if (!countryList.length) {
        return (
            <SectionPanel title={t("pages:settingsPage.billingAddress.title")}>
                <Box className={classes.loadingContainer}>
                    <LoadingIcon size="block" />
                </Box>
            </SectionPanel>
        );
    }

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={profile ?? billingAddress}
            validate={validate}
            render={({ handleSubmit, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                    <SectionPanel
                        title={t("pages:settingsPage.billingAddress.title")}
                        actions={
                            <>
                                <SecondaryButton type="button" onClick={onCancel} disabled={isLoading}>
                                    {t("common:button.cancel")}
                                </SecondaryButton>
                                <PrimaryButton type="submit" disabled={submitting || pristine || isLoading}>
                                    {t("common:button.save")}
                                </PrimaryButton>
                            </>
                        }
                    >
                        <SectionFormStack>
                            <SectionInputSlot>
                                <TextField
                                    name="name"
                                    label={t("pages:editBillingPage.label.fullName")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField name="address" label={t("common:label.address")} fullWidth disabled={isLoading} />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="zipCode"
                                    label={t("pages:editBillingPage.label.zipCode")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="city"
                                    label={t("pages:editBillingPage.label.city")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <ControlledSelectField
                                    name="country"
                                    label={t("pages:editBillingPage.label.country")}
                                    options={countryList}
                                    disabled={isLoading}
                                    searchable
                                />
                            </SectionInputSlot>
                        </SectionFormStack>
                    </SectionPanel>
                </form>
            )}
        />
    );
};
